import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Trophy, AlertTriangle } from 'lucide-react';
import fallbackBadge from './logo.png';

const FOTMOB_LEAGUE_ID = '289'; // AFCON 2025

// ──────────────────────────────────────────────
// Match Card (almost identical to original + penalty support)
// ──────────────────────────────────────────────
const MatchCard = React.memo(({ match, penaltyInfo }) => {
  const getStatusBadge = () => {
    if (match.status === 'live') {
      return (
        <div className="bg-red-600/90 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 animate-pulse border border-red-500/30">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          LIVE{match.minute ? ` ${match.minute}'` : ''}
        </div>
      );
    }
    if (match.status === 'finished') {
      return (
        <div className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-xs font-semibold border border-gray-600">
          FT
        </div>
      );
    }
    return (
      <div className="bg-blue-600/80 text-white px-3 py-1 rounded-full text-xs font-semibold border border-blue-500/30">
        Upcoming
      </div>
    );
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const pens = penaltyInfo?.[match.id];
  const showPenaltyInfo = match.status === 'finished' && match.homeScore === match.awayScore;

  return (
    <Link
      to={`/match/${match.id}`}
      className="bg-gray-900/70 backdrop-blur-md border border-gray-800 rounded-xl overflow-hidden
                 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10
                 block h-full group"
    >
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 font-medium group-hover:text-gray-300">
            {match.group || 'Group Stage'}
          </span>
          {getStatusBadge()}
        </div>

        <div className="flex items-center justify-between gap-3">
          {/* Home */}
          <div className="flex-1 text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto flex items-center justify-center mb-2">
              <img
                src={match.homeBadge}
                alt={match.homeTeam}
                className="max-w-full max-h-full object-contain transition-transform group-hover:scale-105"
                onError={(e) => (e.target.src = fallbackBadge)}
              />
            </div>
            <div className="font-medium text-sm sm:text-base text-white truncate group-hover:text-emerald-300">
              {match.homeTeam}
            </div>
          </div>

          {/* Center - score / vs / penalties */}
          <div className="text-center min-w-[100px]">
            {match.status === 'upcoming' ? (
              <>
                <div className="text-xl sm:text-2xl font-bold text-gray-400 mb-1 group-hover:text-gray-300">VS</div>
                <div className="text-xs text-gray-500 group-hover:text-gray-400">
                  {formatDate(match.kickoffTime)}
                </div>
              </>
            ) : (
              <div>
                <div className="text-2xl sm:text-3xl font-black text-emerald-400 tracking-wider group-hover:text-emerald-300">
                  {match.homeScore ?? '-'} - {match.awayScore ?? '-'}
                </div>

                {showPenaltyInfo && (
                  <div className="mt-1 text-xs">
                    {pens ? (
                      <>
                        {pens.aggregate ? (
                          <span className="font-bold text-yellow-300">
                            {pens.aggregate}  {/* shows "Pen 2 - 4" */}
                          </span>
                        ) : pens.homePens != null ? (
                          <span className="font-semibold text-yellow-400">
                            {pens.homePens}–{pens.awayPens} pens → {pens.winner} 🏆
                          </span>
                        ) : (
                          <span className="font-medium text-yellow-300">
                            Shootout • {pens.winner || 'Decided on pens'}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500 italic animate-pulse">
                        Loading shootout...
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Away */}
          <div className="flex-1 text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto flex items-center justify-center mb-2">
              <img
                src={match.awayBadge}
                alt={match.awayTeam}
                className="max-w-full max-h-full object-contain transition-transform group-hover:scale-105"
                onError={(e) => (e.target.src = fallbackBadge)}
              />
            </div>
            <div className="font-medium text-sm sm:text-base text-white truncate group-hover:text-emerald-300">
              {match.awayTeam}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-black/40 px-4 py-3 text-xs text-gray-400 flex flex-wrap gap-x-6 gap-y-1 border-t border-gray-800/70 backdrop-blur-sm">
        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-emerald-400" />
          <span>{formatDate(match.kickoffTime)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={14} className="text-emerald-400" />
          <span>{formatTime(match.kickoffTime)}</span>
        </div>
        <div className="flex items-center gap-1.5 max-w-[180px]">
          <MapPin size={14} className="text-emerald-400" />
          <span className="truncate">{match.stadium || 'Morocco'}</span>
        </div>
      </div>
    </Link>
  );
});

// ──────────────────────────────────────────────
// Keep your original GroupStandings & KnockoutBracket
// ──────────────────────────────────────────────
const GroupStandings = ({ groups }) => {
  if (!groups || groups.length === 0) return null;
  return (
    <section className="mb-14 md:mb-20">
      <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8 flex items-center gap-3">
        <Trophy className="text-emerald-400" />
        Group Standings
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((group, idx) => (
          <div key={idx} className="bg-gray-900/70 backdrop-blur-md border border-gray-800 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4 text-emerald-300">
              {group.groupName || `Group ${String.fromCharCode(65 + idx)}`}
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  <th className="text-left py-2">Team</th>
                  <th className="text-center">P</th>
                  <th className="text-center">W</th>
                  <th className="text-center">D</th>
                  <th className="text-center">L</th>
                  <th className="text-center">Pts</th>
                </tr>
              </thead>
              <tbody>
                {group.table.map((team, tIdx) => (
                  <tr key={tIdx} className="border-b border-gray-800 last:border-0">
                    <td className="py-3 flex items-center gap-3">
                      <img
                        src={`https://images.fotmob.com/image_resources/logo/teamlogo/${team.teamId}.png`}
                        alt={team.teamName}
                        className="w-6 h-6 object-contain"
                      />
                      <span className="text-white font-medium">{team.teamName}</span>
                    </td>
                    <td className="text-center text-gray-300">{team.played}</td>
                    <td className="text-center text-gray-300">{team.wins}</td>
                    <td className="text-center text-gray-300">{team.draws}</td>
                    <td className="text-center text-gray-300">{team.losses}</td>
                    <td className="text-center font-bold text-emerald-400">{team.pts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
};

const KnockoutBracket = ({ rounds }) => {
  if (!rounds || rounds.length === 0) return null;
  return (
    <section className="mb-14 md:mb-20">
      <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8 flex items-center gap-3">
        <Trophy className="text-emerald-400" />
        Knockout Stage
      </h2>
      {/* Your original knockout bracket rendering logic here */}
      {/* ... (you can paste your original KnockoutBracket content) ... */}
    </section>
  );
};

// ──────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────
export const MatchesPage = () => {
  const [matches, setMatches] = useState([]);
  const [groups, setGroups] = useState([]);
  const [knockoutRounds, setKnockoutRounds] = useState([]);
  const [penaltyInfo, setPenaltyInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchPenaltyDetails = useCallback(async (matchId) => {
    if (penaltyInfo[matchId]) return;

    try {
      const res = await fetch(`https://www.fotmob.com/api/matchDetails?matchId=${matchId}`);
      if (!res.ok) return;
      const data = await res.json();

      const status = data?.header?.status || {};
      const reason = status.reason || {};

      // Primary source: the most reliable place in 2026 responses
      if (reason.penalties && Array.isArray(reason.penalties) && reason.penalties.length === 2) {
        const [homePens, awayPens] = reason.penalties;
        const winner =
          homePens > awayPens ? data.header.teams[0].name :
          awayPens > homePens ? data.header.teams[1].name : 'Draw';

        setPenaltyInfo(prev => ({
          ...prev,
          [matchId]: {
            homePens,
            awayPens,
            winner,
            aggregate: reason.long || status.scoreAggregateStr || null  // e.g. "Pen 2 - 4"
          }
        }));
        return;
      }

      // Fallback 1: penaltyScore from PenaltyShootout event
      const shootoutEvent = data?.events?.find(e => e.type === 'PenaltyShootout');
      if (shootoutEvent?.penaltyScore && Array.isArray(shootoutEvent.penaltyScore)) {
        const [homePens, awayPens] = shootoutEvent.penaltyScore;
        const winner =
          homePens > awayPens ? data.header.teams[0].name :
          awayPens > homePens ? data.header.teams[1].name : 'Draw';

        setPenaltyInfo(prev => ({
          ...prev,
          [matchId]: {
            homePens,
            awayPens,
            winner,
            aggregate: reason.long || status.scoreAggregateStr || null
          }
        }));
        return;  // ← stop here if we found it
      }

      // Fallback 2: just use the long reason string if numbers missing
      if (reason.long?.includes('Pen')) {
        setPenaltyInfo(prev => ({
          ...prev,
          [matchId]: {
            homePens: null,
            awayPens: null,
            winner: status.whoLostOnPenalties ? `${status.whoLostOnPenalties} lost` : null,
            aggregate: reason.long  // "Pen 2 - 4"
          }
        }));
      }

    } catch (err) {
      console.error('Penalty fetch error:', err);
    }
  }, [penaltyInfo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`https://www.fotmob.com/api/leagues?id=${FOTMOB_LEAGUE_ID}`);
        if (!response.ok) throw new Error('API error');
        const json = await response.json();

        let allMatches = json.fixtures?.allMatches ||
                        json.leagueOverviewMatches ||
                        json.matches?.allMatches ||
                        json.fixtures || [];

        const mapped = allMatches
          .map(m => {
            const statusObj = m.status || {};
            const utcTime = statusObj.utcTime || m.date || new Date().toISOString();

            let id = m.id || m.matchId || '';
            if (!/^\d+$/.test(id)) {
              const hash = (m.pageUrl || '').match(/#(\d+)$/);
              id = hash ? hash[1] : null;
            }
            if (!id) return null;

            return {
              id: parseInt(id),
              group: m.group || m.tournamentStage || m.roundName || 'Group Stage',
              homeTeam: m.home?.name || 'Home',
              awayTeam: m.away?.name || 'Away',
              homeBadge: m.home?.id ? `https://images.fotmob.com/image_resources/logo/teamlogo/${m.home.id}.png` : fallbackBadge,
              awayBadge: m.away?.id ? `https://images.fotmob.com/image_resources/logo/teamlogo/${m.away.id}.png` : fallbackBadge,
              homeScore: statusObj.scoreStr ? parseInt(statusObj.scoreStr.split(' - ')[0]) : null,
              awayScore: statusObj.scoreStr ? parseInt(statusObj.scoreStr.split(' - ')[1]) : null,
              kickoffTime: utcTime,
              stadium: m.venue?.name || m.venueName || 'Morocco',
              status: statusObj.finished ? 'finished' :
                      (statusObj.started || statusObj.live) ? 'live' : 'upcoming',
              minute: statusObj.liveTime?.short?.replace(/[^0-9+]/g, '') || null,
            };
          })
          .filter(Boolean);

        // Sort
        mapped.sort((a, b) => {
          if (a.status === 'live') return -1;
          if (b.status === 'live') return 1;
          if (a.status === 'upcoming' && b.status === 'upcoming') return new Date(a.kickoffTime) - new Date(b.kickoffTime);
          if (a.status === 'finished' && b.status === 'finished') return new Date(b.kickoffTime) - new Date(a.kickoffTime);
          return a.status === 'upcoming' ? -1 : 1;
        });

        setMatches(mapped);

        // Queue penalty checks for likely shootout matches
        mapped.forEach(match => {
          if (
            match.status === 'finished' &&
            match.homeScore === match.awayScore &&
            /round|quarter|semi|final|knockout/i.test(match.group)
          ) {
            fetchPenaltyDetails(match.id);
          }
        });

        // Groups & Knockout
        const tableData = json.table?.[0]?.data || {};
        setGroups(tableData.groups || (tableData.table ? [{ table: tableData.table }] : []));

        const knockout = json.knockout || {};
        setKnockoutRounds(knockout.rounds || []);

      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchPenaltyDetails]);

  const filteredMatches = 
    activeFilter === 'all' ? matches :
    activeFilter === 'live' ? matches.filter(m => m.status === 'live') :
    activeFilter === 'upcoming' ? matches.filter(m => m.status === 'upcoming') :
    matches.filter(m => m.status === 'finished');

  if (loading) return <div className="min-h-screen flex items-center justify-center text-emerald-400">Loading...</div>;
  if (error || !matches.length) return <div className="min-h-screen flex items-center justify-center text-gray-400">Data unavailable</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-950 text-white pb-16">
      <div className="container mx-auto px-4 sm:px-6 pt-8 md:pt-12 lg:pt-16">
        {/* Your header, filters, banner... */}
        
        <GroupStandings groups={groups} />
        <KnockoutBracket rounds={knockoutRounds} />

        <section className="mb-14 md:mb-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8">Matches</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredMatches.map(match => (
              <MatchCard key={match.id} match={match} penaltyInfo={penaltyInfo} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MatchesPage;